import { ref, computed, watch } from 'vue'

// ============================================================================
// CONSTANTES
// ============================================================================
const THEME_MODES = {
  AUTO: 'auto',
  LIGHT: 'light',
  DARK: 'dark',
}

const STORAGE_KEY = 'theme-mode'

// ============================================================================
// ESTADO GLOBAL REACTIVO (compartido entre componentes)
// ============================================================================
const themeMode = ref(THEME_MODES.AUTO) // 'auto' | 'light' | 'dark'
const systemPrefersDark = ref(false)
let mediaQueryListener = null

// ============================================================================
// COMPOSABLE
// ============================================================================
export function useTheme() {
  // Computed: determina si el tema actual es oscuro
  const isDark = computed(() => {
    if (themeMode.value === THEME_MODES.AUTO) {
      return systemPrefersDark.value
    }
    return themeMode.value === THEME_MODES.DARK
  })

  // Computed: icono actual basado en el modo
  const currentIcon = computed(() => {
    switch (themeMode.value) {
      case THEME_MODES.AUTO:
        return 'auto'
      case THEME_MODES.LIGHT:
        return 'sun'
      case THEME_MODES.DARK:
        return 'moon'
      default:
        return 'auto'
    }
  })

  // Computed: label del modo actual
  const currentModeLabel = computed(() => {
    switch (themeMode.value) {
      case THEME_MODES.AUTO:
        return 'Auto'
      case THEME_MODES.LIGHT:
        return 'Light'
      case THEME_MODES.DARK:
        return 'Dark'
      default:
        return 'Auto'
    }
  })

  /**
   * Aplica la clase 'dark' al documento y añade transición suave
   */
  const applyTheme = () => {
    const html = document.documentElement

    // Agregar clase de transición para cambio suave
    html.classList.add('theme-transition')

    if (isDark.value) {
      html.classList.add('dark')
    } else {
      html.classList.remove('dark')
    }

    // Remover clase de transición después de completar
    setTimeout(() => {
      html.classList.remove('theme-transition')
    }, 300)
  }

  /**
   * Establece el modo del tema
   * @param {'auto' | 'light' | 'dark'} mode
   */
  const setTheme = (mode) => {
    if (Object.values(THEME_MODES).includes(mode)) {
      themeMode.value = mode
    }
  }

  /**
   * Cicla entre los modos: auto → light → dark → auto
   */
  const cycleTheme = () => {
    const modes = [THEME_MODES.AUTO, THEME_MODES.LIGHT, THEME_MODES.DARK]
    const currentIndex = modes.indexOf(themeMode.value)
    const nextIndex = (currentIndex + 1) % modes.length
    themeMode.value = modes[nextIndex]
  }

  /**
   * Toggle simple entre light y dark (para compatibilidad)
   */
  const toggleTheme = () => {
    if (isDark.value) {
      setTheme(THEME_MODES.LIGHT)
    } else {
      setTheme(THEME_MODES.DARK)
    }
  }

  /**
   * Inicializa el sistema de temas
   * - Detecta preferencia del sistema
   * - Carga preferencia guardada en localStorage
   * - Configura listener para cambios del sistema
   */
  const initTheme = () => {
    // Detectar preferencia del sistema
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    systemPrefersDark.value = mediaQuery.matches

    // Listener para cambios en la preferencia del sistema
    mediaQueryListener = (e) => {
      systemPrefersDark.value = e.matches
      // Si está en modo auto, aplicar el tema automáticamente
      if (themeMode.value === THEME_MODES.AUTO) {
        applyTheme()
      }
    }
    mediaQuery.addEventListener('change', mediaQueryListener)

    // Cargar preferencia guardada
    const savedMode = localStorage.getItem(STORAGE_KEY)
    if (savedMode && Object.values(THEME_MODES).includes(savedMode)) {
      themeMode.value = savedMode
    } else {
      // Por defecto, usar modo automático
      themeMode.value = THEME_MODES.AUTO
    }

    // Aplicar tema inicial
    applyTheme()
  }

  /**
   * Limpia los listeners (para cleanup en onUnmounted)
   */
  const cleanup = () => {
    if (mediaQueryListener) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      mediaQuery.removeEventListener('change', mediaQueryListener)
    }
  }

  // Watcher para persistir y aplicar cambios
  watch(themeMode, (newMode) => {
    localStorage.setItem(STORAGE_KEY, newMode)
    applyTheme()
  })

  // Watcher para cambios en preferencia del sistema cuando está en auto
  watch(systemPrefersDark, () => {
    if (themeMode.value === THEME_MODES.AUTO) {
      applyTheme()
    }
  })

  return {
    // Estado
    themeMode,
    isDark,
    currentIcon,
    currentModeLabel,

    // Constantes
    THEME_MODES,

    // Métodos
    setTheme,
    cycleTheme,
    toggleTheme,
    initTheme,
    cleanup,
  }
}
